import {usePromotion} from '@/context/Promotion/PromotionContext'
import {
  CurrencyType,
  DiscountReward,
  PromotionCondition,
  PromotionType,
  RewardType,
  type DiscountType,
} from '@/types/promotion'
import {DiscountType as DiscountTypeEnum} from '@/types/promotionTypes.js'
import {useSelector} from 'react-redux'
import {toRewardsUpdate} from './toRewardsUpdate'
import {useRewardValidations} from './RewardValidations.js'

type DiscountField = 'discountValue' | 'discountAmountUpto'

type UseRewardDiscountArgs = {
  promotionCondition: PromotionCondition
  showDynamicPlaceholder?: boolean
  discountType?: DiscountType
  rewardIndex?: number
}

const percentageDiscountTypes = new Set<string>([
  DiscountTypeEnum.PERCENTAGE,
  PromotionType.STORE_DISCOUNT,
  PromotionType.WELCOME_DISCOUNT,
])

const limitToTwoDecimals = (value: string) => {
  if (!value) return value

  const normalizedValue = value.replace(',', '.')
  const [integerPart, decimalPart] = normalizedValue.split('.')

  if (decimalPart === undefined) {
    return normalizedValue
  }

  return `${integerPart}.${decimalPart.slice(0, 2)}`
}

export const useRewardDiscount = ({
  promotionCondition,
  showDynamicPlaceholder = false,
  discountType,
  rewardIndex = 0,
}: UseRewardDiscountArgs) => {
  const {updateCondition, promotionConfig, promotion} = usePromotion()
  const maxDiscountPercentage = promotionConfig?.maxDiscountPercentage || 0
  const {countryCode} = useSelector((state: any) => state?.countryCode)
  const isIndia = countryCode == 'IND'
  const currencyCode = CurrencyType[countryCode ?? 'MX'] ?? '$'
  const currentReward = promotionCondition?.rewards?.[rewardIndex]

  const currentDiscountReward =
    currentReward?.type === RewardType.DISCOUNT ? (currentReward as DiscountReward) : undefined

  const isCashDiscount = currentDiscountReward?.discountType === DiscountTypeEnum.AMOUNT
  const isPercentageDiscount = percentageDiscountTypes.has(
    currentDiscountReward?.discountType ?? DiscountTypeEnum.PERCENTAGE
  )
  const currentRewardDiscountAmountUpto = currentDiscountReward?.discountAmountUpto || ''
  const {hasValueLayerError} = useRewardValidations(promotionCondition, rewardIndex)
  const resolvedDiscountType = (discountType ??
    currentDiscountReward?.discountType ??
    DiscountTypeEnum.PERCENTAGE) as DiscountType

  const updateDiscountReward = (field: DiscountField, value: string) => {
    const baseReward: DiscountReward = currentDiscountReward ?? {
      type: RewardType.DISCOUNT,
      discountType: resolvedDiscountType,
      discountValue: 0,
    }

    const maxDiscountAmountUptoUpdate = value === '' ? {discountAmountUpto: null} : {discountAmountUpto: Number(value)}

    const rewardFieldUpdate = field === 'discountValue' ? {discountValue: Number(value)} : maxDiscountAmountUptoUpdate

    const nextReward: DiscountReward = {
      ...baseReward,
      discountType: resolvedDiscountType,
      ...rewardFieldUpdate,
    }

    updateCondition({
      sequence: promotionCondition.sequence,
      rewards: toRewardsUpdate(nextReward, rewardIndex),
    })
  }

  const createDiscountChangeHandler = (field: DiscountField) => (value: string) => {
    updateDiscountReward(field, value === '' ? value : limitToTwoDecimals(value))
  }

  const handleDiscountChange = createDiscountChangeHandler('discountValue')
  const handleMaxDiscountChange = createDiscountChangeHandler('discountAmountUpto')

  const placeholderText = showDynamicPlaceholder && !isCashDiscount ? `Up to ${maxDiscountPercentage}%` : '0.0'
  const discountPercentage = currentDiscountReward?.discountValue || ''

  // Don't show error for STORE_DISCOUNT and WELCOME_DISCOUNT promotion types
  const isError =
    (promotion.promotionType !== PromotionType.STORE_DISCOUNT &&
      promotion.promotionType !== PromotionType.WELCOME_DISCOUNT &&
      discountType !== DiscountTypeEnum.AMOUNT &&
      discountPercentage > maxDiscountPercentage) ||
    (typeof discountPercentage === 'number' && discountPercentage < 0.01)
  const isLayerError = hasValueLayerError()
  // A blank-space error flags the field as invalid without showing a message
  const errorText = isLayerError ? 'Value Invalid' : isError ? ' ' : ''
  // @ts-expect-error discountAmountUpto may be '' when unset
  const maxDiscountError = currentRewardDiscountAmountUpto < 0 ? ' ' : ''

  return {
    currencyCode,
    isCashDiscount,
    isPercentageDiscount,
    placeholderText,
    decimalLimit: isIndia ? 2 : 4,
    discountValue: limitToTwoDecimals(discountPercentage.toString()),
    maxDiscountValue: currentRewardDiscountAmountUpto.toString().replace(',', '.'),
    errorText,
    maxDiscountError,
    hasErrorWithoutMessage: Boolean(isError || maxDiscountError),
    handleDiscountChange,
    handleMaxDiscountChange,
  }
}
